import {
  addPMSColor,
  addRecentlyUsedColor,
  backupBomOptions,
  changeCurrentPreviewElementsAction,
  changeNextgenUiStatus,
  changeUploadingFileStatus,
  cleanUpRecentBomOptionsList,
  clearBomOptionsBackup,
  clearCurrentEditedBomOption,
  closeImprintRuleChangeConfirmModal,
  expandImprintFlyout,
  finishRequestPreview,
  foldImprintFlyout,
  initNextgenStepSettings,
  restoreRecentBomOptionsList,
  setAlertInfo,
  setBackupColorCharge,
  setBackupElementsAction,
  setBackupTextColor,
  setBackupTextOutlineColor,
  setCurrentBomOption,
  setCurrentEditedBomOption,
  setCurrentGalleryItem,
  setFlyoutPanelHeight,
  setFreeColor,
  setIsMandatorySpecialImageWhenOnlyOneDict,
  setNextgenCurrentStep,
  setNextgenUi,
  setPreviewPdfBlob,
  setProductDetailsInterfaceSettings,
  setShowColorChangeConfirmation,
  setShowImageDownloaderConfirmMessage,
  setShowSaveProjectModal,
  setShowSaveProjectSucessMessage,
  showImprintRuleChangeConfirmModal,
  startRequestPreview,
  switchShowPreviewWindow,
  updateRecentBomOptionsList,
  updateSharePanel,
} from './actions';
import { createReducer, current } from '@reduxjs/toolkit';
import { getPanelChain, isPanelInChain } from './logics';
import { nextGenFlyoutPanelEnum, shareContainerUITypeEnum, shareContentTypeEnum } from './constants';
import { nextgenProductDetailModels, nextgenUiSteps } from '../main/constants';

const uiInitState = {
  flyoutPanel: { currentPanel: nextGenFlyoutPanelEnum.None, show: true },
  flyoutPanelChain: null,
  currentPreviewElements: {
    image: [],
    text: [],
  },
  previewPdfBlob: {
    blob: '',
    size: 0,
    showConfirmMessage: false,
  },
  sharePanel: {
    uiType: shareContainerUITypeEnum.Modal,
    contentType: shareContentTypeEnum.Email,
    show: false,
  },
  showPreviewWindow: false,
  showSaveProjectModal: false,
  imprintFlyout: { expand: false },
  ruleChangeConfirmModel: { show: false, content: {} },
  showImageDownloaderConfirmMessage: false,
  showSaveProjectSucessMessage: false,
  productDetailsInterfaceBuffer: {
    bomOptionsBackup: null,
    currentBomOption: null,
    currentEditedBomOption: null,
    currentGalleryItem: '',
    recentBomOptions: [],
  },
  productDetailsInterfaceSettings: {
    editingMode: false,
    showAsModal: false,
    showing: false,
  },
  stepSettings: {
    nextgenStep: nextgenUiSteps.Customize,
    productDetailMode: nextgenProductDetailModels.Modal,
    steps: {},
  },
  userEnteringText: false,
  alertInfo: { show: false, showCloseBtn: true, variant: 'success', message: '' },
  backupTextColor: {},
  backupTextOutlineColor: {},
  showColorChangeConfirmation: true,
  isRequestingPreview: false,
  backupElements: { text: [], image: [] },
  customPMSColors: [],
  backupColorCharge: null,
  recentlyUsedColors: [],
  freeColor: null,
  isMandatorySpecialImageWhenOnlyOneDict: {},
};

export const nextgenUi = createReducer(uiInitState, (builder) => {
  builder
    .addCase(setNextgenUi, (state, action) => {
      Object.assign(state, action.payload);
    })
    .addCase(changeNextgenUiStatus, (state, action) => {
      if (action.payload.flyoutPanel !== undefined) {
        const flyoutPanelChain = state.flyoutPanelChain && current(state.flyoutPanelChain);
        if (!flyoutPanelChain || !isPanelInChain(flyoutPanelChain, action.payload.flyoutPanel.currentPanel)) {
          state.flyoutPanel = action.payload.flyoutPanel; // set the whole object
        } else {
          state.flyoutPanel.currentPanel = action.payload.flyoutPanel.currentPanel;

          if (action.payload.flyoutPanel.currentPanel === nextGenFlyoutPanelEnum.None) {
            // close a chain panel
            const restChain = getPanelChain(flyoutPanelChain, action.payload.flyoutPanel.currentPanel, true);
            state.flyoutPanel.stackPanelChain = restChain; // restChain maybe null
          } else {
            const chain = getPanelChain(flyoutPanelChain, action.payload.flyoutPanel.currentPanel, false);
            state.flyoutPanel.stackPanelChain = chain;
          }
        }
      }
      if (action.payload.flyoutPanelChain !== undefined) {
        state.flyoutPanelChain = action.payload.flyoutPanelChain;
      }
      if (action.payload.isReseting !== undefined) state.isReseting = action.payload.isReseting;
      if (action.payload.showCanvasGrid !== undefined) state.showCanvasGrid = action.payload.showCanvasGrid;
      if (action.payload.triggerCrop !== undefined) state.triggerCrop = action.payload.triggerCrop;
      if (action.payload.sidebarItemList !== undefined) state.sidebarItemList = action.payload.sidebarItemList;
      if (action.payload.getStartedIconList !== undefined) state.getStartedIconList = action.payload.getStartedIconList;
      if (action.payload.getStartedColor !== undefined) state.getStartedColor = action.payload.getStartedColor;
      if (action.payload.elementToScroll !== undefined) state.elementToScroll = action.payload.elementToScroll;
      if (action.payload.canvasFocusElement !== undefined) {
        state.canvasFocusElement = state.canvasFocusElement || {};
        state.canvasFocusElement[action.payload.canvasKey] = action.payload.canvasFocusElement;
      }
      if (action.payload.isRotating !== undefined) state.isRotating = action.payload.isRotating;
      if (action.payload.canvasContextMenuActions !== undefined)
        state.canvasContextMenuActions = action.payload.canvasContextMenuActions;
      if (action.payload.guid !== undefined && action.payload.outlineRect !== undefined) {
        state.canvasElementOutlineCache = state.canvasElementOutlineCache || [];
        if (state.canvasElementOutlineCache.length > 200) state.canvasElementOutlineCache = [];
        const cachedRect = state.canvasElementOutlineCache.find((x) => x.guid === action.payload.guid);
        if (cachedRect) cachedRect.outlineRect = action.payload.outlineRect;
        else
          state.canvasElementOutlineCache.push({ guid: action.payload.guid, outlineRect: action.payload.outlineRect });
      }
      if (action.payload.isElementResizing !== undefined) state.isElementResizing = action.payload.isElementResizing;
      if (action.payload.isBatchSelecting !== undefined) state.isBatchSelecting = action.payload.isBatchSelecting;
      if (action.payload.needUpdateElementPosition !== undefined)
        state.needUpdateElementPosition = action.payload.needUpdateElementPosition;
      if (action.payload.showCanvasCover !== undefined) {
        state.showCanvasCover = action.payload.showCanvasCover;
      }
      if (action.payload.isRemovingWhite !== undefined) {
        state.isRemovingWhite = action.payload.isRemovingWhite;
      }
      if (action.payload.showUploadCover !== undefined) {
        state.showUploadCover = action.payload.showUploadCover;
      }
      if (action.payload.uploadMultiple !== undefined) {
        if (action.payload.uploadMultiple === null) {
          state.uploadMultiple = null;
          return;
        }
        if (action.payload.uploadMultiple.files !== undefined) {
          state.uploadMultiple = action.payload.uploadMultiple;
        }
      }
      if (action.payload.selectedArtType !== undefined) state.selectedArtType = action.payload.selectedArtType;
      if (action.payload.artGuidToBeReplaced !== undefined)
        state.artGuidToBeReplaced = action.payload.artGuidToBeReplaced;
      if (action.payload.showCanvasGrid !== undefined) state.showCanvasGrid = action.payload.showCanvasGrid;
    })
    .addCase(changeUploadingFileStatus, (state, action) => {
      if (action.payload.file !== undefined && state.uploadMultiple && state.uploadMultiple.files) {
        const file = state.uploadMultiple.files.find(
          (f) => f.name === action.payload.file.name && f.size === action.payload.file.size
        );
        if (file) {
          file.complete = action.payload.complete;
          state.uploadMultiple = state.uploadMultiple || {};
          const count = state.uploadMultiple.uploadMultipleCount || 0;
          state.uploadMultiple.uploadMultipleCount = count + 1;
        }
      }
    })
    .addCase(setFlyoutPanelHeight, (state, action) => {
      state.flyoutPanelHeight = action.payload;
    })
    .addCase(changeCurrentPreviewElementsAction, (state, action) => {
      state.currentPreviewElements = action.payload;
    })
    .addCase(setPreviewPdfBlob, (state, action) => {
      Object.assign(state.previewPdfBlob, { ...action.payload });
    })
    .addCase(updateSharePanel, (state, action) => {
      Object.assign(state.sharePanel, action.payload);
    })
    .addCase(switchShowPreviewWindow, (state, action) => {
      state.showPreviewWindow = action.payload;
    })
    .addCase(expandImprintFlyout, (state, action) => {
      Object.assign(state.imprintFlyout, { expand: true });
    })
    .addCase(foldImprintFlyout, (state, action) => {
      Object.assign(state.imprintFlyout, { expand: false });
    })
    .addCase(showImprintRuleChangeConfirmModal, (state, action) => {
      Object.assign(state.ruleChangeConfirmModel, { show: true, content: action.payload });
    })
    .addCase(closeImprintRuleChangeConfirmModal, (state, action) => {
      Object.assign(state.ruleChangeConfirmModel, { show: false, content: {} });
    })
    .addCase(setShowImageDownloaderConfirmMessage, (state, action) => {
      Object.assign(state, action.payload);
    })
    .addCase(setShowSaveProjectSucessMessage, (state, action) => {
      Object.assign(state, action.payload);
    })
    .addCase(backupBomOptions, (state, action) => {
      state.productDetailsInterfaceBuffer.bomOptionsBackup = action.payload;
    })
    .addCase(clearBomOptionsBackup, (state, action) => {
      state.productDetailsInterfaceBuffer.bomOptionsBackup = null;
    })
    .addCase(setCurrentBomOption, (state, action) => {
      state.productDetailsInterfaceBuffer.currentBomOption = action.payload;
    })
    .addCase(clearCurrentEditedBomOption, (state, action) => {
      state.productDetailsInterfaceBuffer.currentEditedBomOption = null;
    })
    .addCase(setCurrentEditedBomOption, (state, action) => {
      state.productDetailsInterfaceBuffer.currentEditedBomOption = action.payload;
    })
    .addCase(setCurrentGalleryItem, (state, action) => {
      state.productDetailsInterfaceBuffer.currentGalleryItem = action.payload;
    })
    .addCase(cleanUpRecentBomOptionsList, (state, action) => {
      const selectedOptions = action.payload;
      state.productDetailsInterfaceBuffer.recentBomOptions =
        state.productDetailsInterfaceBuffer.recentBomOptions.filter((recentOption) =>
          selectedOptions.some(
            (option) => option.code === recentOption.code && option.codeValue === recentOption.codeValue
          )
        );
    })
    .addCase(restoreRecentBomOptionsList, (state, action) => {
      state.productDetailsInterfaceBuffer.recentBomOptions = action.payload;
    })
    .addCase(updateRecentBomOptionsList, (state, action) => {
      const { multiSelect, option } = action.payload;
      if (multiSelect) {
        if (option.isSelected) {
          state.productDetailsInterfaceBuffer.recentBomOptions.push(option);
        } else {
          const index = state.productDetailsInterfaceBuffer.recentBomOptions.findIndex(
            (recentOption) => recentOption.code === option.code && recentOption.codeValue === option.codeValue
          );
          if (index >= 0) {
            state.productDetailsInterfaceBuffer.recentBomOptions.splice(index, 1);
          }
        }
      } else {
        state.productDetailsInterfaceBuffer.recentBomOptions = option.isSelected ? [option] : [];
      }
    })
    .addCase(setProductDetailsInterfaceSettings, (state, action) => {
      state.productDetailsInterfaceSettings = {
        ...state.productDetailsInterfaceSettings,
        ...action.payload,
      };
    })
    .addCase(initNextgenStepSettings, (state, action) => {
      state.stepSettings = action.payload;
    })
    .addCase(setNextgenCurrentStep, (state, action) => {
      Object.assign(state.stepSettings, { nextgenStep: action.payload });
    })
    .addCase(setShowSaveProjectModal, (state, action) => {
      state.showSaveProjectModal = action.payload;
    })
    .addCase(setAlertInfo, (state, action) => {
      //allow payload to contain fields of alertInfo object
      //show, showCloseBtn, variant, message, timer
      state.alertInfo = action.payload;
    })
    .addCase(setBackupTextColor, (state, action) => {
      const { textGuid, textColor } = action.payload;

      state.backupTextColor[textGuid] = textColor;
    })
    .addCase(setBackupTextOutlineColor, (state, action) => {
      const { textGuid, textOutlineColor } = action.payload;

      state.backupTextOutlineColor[textGuid] = textOutlineColor;
    })
    .addCase(setBackupColorCharge, (state, action) => {
      state.backupColorCharge = action.payload;
    })
    .addCase(setShowColorChangeConfirmation, (state, action) => {
      const { show } = action.payload;
      state.showColorChangeConfirmation = show;
    })
    .addCase(startRequestPreview, (state, action) => {
      state.isRequestingPreview = true;
    })
    .addCase(finishRequestPreview, (state, action) => {
      state.isRequestingPreview = false;
    })
    .addCase(setBackupElementsAction, (state, action) => {
      const { texts, images } = action.payload;
      state.backupElements.text = texts;
      state.backupElements.image = images;
    })
    .addCase(addPMSColor, (state, action) => {
      const pmsColor = action.payload;
      const pmsColors = state.customPMSColors;
      state.customPMSColors = [pmsColor, ...pmsColors.filter((c) => c.colorId !== pmsColor.colorId)];
    })
    .addCase(addRecentlyUsedColor, (state, action) => {
      const color = action.payload;

      const allUsedColors = state.recentlyUsedColors.filter(
        (f) => f.colorId !== color.colorId //&& f.colorReference !== color.colorReference
      );

      // -- Add color to the top
      state.recentlyUsedColors = [color, ...allUsedColors];
    })
    .addCase(setFreeColor, (state, action) => {
      const color = action.payload;
      state.freeColor = color;
    })
    .addCase(setIsMandatorySpecialImageWhenOnlyOneDict, (state, action) => {
      const { imprintAreaKey, isMandatory } = action.payload;
      state.isMandatorySpecialImageWhenOnlyOneDict[imprintAreaKey] = isMandatory;
    });
});
